import * as React from "react";
import { connect } from "react-redux";

import * as moment from "moment";

import { Appointment, AppointmentAndParent } from "@teamprevent/common/calendar/models";

import { loadOhCaseDetail } from "../../crud/ohCase/actions";
import { ApplicationState } from "../models";
import { OhCaseState } from "../../crud/ohCase";
import { routeDateSelector } from "../selectors";
import { EditModal as ClinicEditModal } from "../clinics/components/EditModal";
import { TaskActionSubRoutes } from "../../task/taskActions/TaskActionSubRoutes";
import { ClinicHealthSurveillanceEmployeeQuestionnaire } from "../../healthSurveillance/components/ClinicHealthSurveillanceEmployeeQuestionnaire";
import { CreateTimeNoteModal } from "../timeNotes/components/CreateTimeNoteModal";
import { updateCalendarSelectedUserIds, updateCalendarSelectedRoomIds } from "../actions";
import { BookAdHocClinicAppointmentModal } from "../../bookAppointments/components/BookAdHocClinicAppointmentModal";
import { Dispatch } from "../../store";
import { MergedAppointmentModal } from "../../bookAppointments/appointmentMerging/MergedAppointmentModal";
import {
    ClinicHealthSurveillanceRepeatConsultationForAppointment
} from "../../healthSurveillance/components/ClinicHealthSurveillanceRepeatConsultationForAppointment";

import { ClinicHealthSurveillanceConsultationForAppointment } from "../../healthSurveillance/components/ClinicHealthSurveillanceConsultationForAppointment";

import { AppointmentModal } from "./AppointmentModal";
import { Calendar } from "./Calendar";

import { replace, Fragment, push, Location } from "redux-little-router";

interface StateProps {
    startDate: moment.Moment;
    selectedRoomIds: string[];
    selectedUserIds: string[];
}

interface DispatchProps {
    updateRouteWithNewDate: (startDate: moment.Moment) => void;
    loadOhCase: (customerId: string, ohCaseId: string) => void;
    selectAppointment: (appointment: AppointmentAndParent, startDate: moment.Moment) => void;
    closeAppointment: (startDate: moment.Moment) => void;
    selectClinic: (clinic: Appointment, startDate: moment.Moment) => void;
    createTimeNoteAppointment: (startDate: moment.Moment, time: moment.Moment, clinicianId: string) => void;
    onSelectedRoomIdsChanged: (value: string[]) => void;
    onSelectedUserIdsChanged: (value: string[]) => void;
}

interface State {
    selectedAppointment?: AppointmentAndParent;
}

class RoomAndAdminUserCalendarUnconnected extends React.Component<StateProps & DispatchProps, State> {

    constructor(props: StateProps & DispatchProps) {
        super(props);
        this.onAppointmentClick = this.onAppointmentClick.bind(this);
        this.onAppointmentModalClose = this.onAppointmentModalClose.bind(this);
    }

    public state: State = {};

    public render() {
        return (
            <>
                <Fragment forRoute="/:date/clinic/:clinicId/appointment/:appointmentId/questionnaire" withConditions={this.isAppointmentRoute}>
                    <ClinicHealthSurveillanceEmployeeQuestionnaire />
                </Fragment>
                <Fragment
                    forRoute="/:date/clinic/:clinicId/appointment/:appointmentId/consultation/:taskId/repeatMedical"
                    withConditions={this.isAppointmentRouteWithTaskId}
                >
                    <ClinicHealthSurveillanceRepeatConsultationForAppointment />
                </Fragment>
                <Fragment forRoute="/:date/clinic/:clinicId/appointment/:appointmentId/consultation" withConditions={this.isAppointmentRoute}>
                    <ClinicHealthSurveillanceConsultationForAppointment />
                </Fragment>
                <Fragment forRoute="/:date/clinic/:clinicId/book-ad-hoc-clinic-appointment">
                    <BookAdHocClinicAppointmentModal
                        startTime={this.state.selectedAppointment?.start}
                        endTime={this.state.selectedAppointment?.end}
                        minStartTime={this.state.selectedAppointment?.parentAppointment?.start}
                        maxEndTime={this.state.selectedAppointment?.parentAppointment?.end}
                        adminUsers={this.state.selectedAppointment?.teamPreventUsers}
                        clinicId={this.state.selectedAppointment?.parentAppointment?.id}
                    />
                </Fragment>
                <Fragment forRoute="/:date/appointment/:appointmentId/:customerId/:ohCaseId" withConditions={this.isTaskRoute}>
                    <TaskActionSubRoutes />
                </Fragment>
                <Fragment forRoute="/:date/mergedappointment/:appointmentId/:customerId/:ohCaseId">
                    <MergedAppointmentModal onModalClose={this.onAppointmentModalClose} />
                </Fragment>
                <Fragment forRoute="/:date/create-time-note" >
                    <CreateTimeNoteModal />
                </Fragment>
                <Fragment forRoute="/" withConditions={this.isNotTaskRoute}>
                    <>
                        <Calendar
                            selectedRoomIds={this.props.selectedRoomIds}
                            selectedUserIds={this.props.selectedUserIds}
                            onSelectedRoomIdsChanged={this.props.onSelectedRoomIdsChanged}
                            onSelectedUserIdsChanged={this.props.onSelectedUserIdsChanged}
                            onDateChange={this.props.updateRouteWithNewDate}
                            onAppointmentClick={this.onAppointmentClick}
                            onHourClick={this.onHourClick}
                            showAllUsersOption={true}
                            selectCurrentUserOnInit={true}
                        />
                        <AppointmentModal onModalClose={this.onAppointmentModalClose} />
                        <ClinicEditModal />
                    </>
                </Fragment>
            </>
        );
    }

    public onAppointmentClick(appointment: AppointmentAndParent) {
        this.setState({ selectedAppointment: appointment });
        if (appointment.clinicType) {
            this.props.selectClinic(appointment, this.props.startDate);
        } else {
            this.props.selectAppointment(appointment, this.props.startDate);
        }
    }

    public onAppointmentModalClose() {
        this.props.closeAppointment(this.props.startDate);
    }

    private onHourClick = (hour: moment.Moment, entityId: string) => {
        this.props.createTimeNoteAppointment(this.props.startDate, hour, entityId);
    };

    private isAppointmentRoute = (location: Location) => !!location.params && !!location.params.appointmentId;

    private isAppointmentRouteWithTaskId = (location: Location) => !!location.params && !!location.params.appointmentId && !!location.params.taskId;

    private isTaskRoute = (l: Location) => !!l.params && !!l.params.taskId;

    private isNotTaskRoute = (l: Location) => !!l.params && !l.params.taskId;
}

const mapStateToProps = (state: ApplicationState & OhCaseState): StateProps => ({
    startDate: routeDateSelector(state),
    selectedRoomIds: state.calendarFilters.roomIds,
    selectedUserIds: state.calendarFilters.userIds,
});

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
    updateRouteWithNewDate: (startDate: moment.Moment) => dispatch(replace(`/calendar/${startDate.format(moment.HTML5_FMT.DATE)}`)),
    loadOhCase: (customerId: string, ohCaseId: string) => dispatch(loadOhCaseDetail({ ohCaseId, customerId })),
    selectAppointment: (appointment, startDate) =>
    {
        if (appointment.customer?.id && appointment.ohCaseId) {

            if (appointment.baseAppointment)
            {
                dispatch(push(
                    /* eslint-disable-next-line max-len */
                    `/calendar/${startDate.format(moment.HTML5_FMT.DATE)}/mergedappointment/${appointment.id}/${appointment.customer.id}/${appointment.ohCaseId}`
                ));

                return;
            }

            dispatch(push(
                /* eslint-disable max-len */
                `/calendar/${startDate.format(moment.HTML5_FMT.DATE)}/appointment/${appointment.id}/${appointment.customer.id}/${appointment.ohCaseId}`));
        } else if (appointment.id && !appointment.parentAppointment) {
            dispatch(push(`/calendar/${startDate.format(moment.HTML5_FMT.DATE)}/appointment/${appointment.id}`));
        } else if (!appointment.id && appointment.parentAppointment?.id) {
            dispatch(push(`/calendar/${startDate.format(moment.HTML5_FMT.DATE)}/clinic/${appointment.parentAppointment?.id}/book-ad-hoc-clinic-appointment`));
        } else if (appointment.id && appointment.parentAppointment?.id) {
            dispatch(push(`/calendar/${startDate.format(moment.HTML5_FMT.DATE)}/clinic/${appointment.parentAppointment?.id}/appointment/${appointment.id}`));
        }
    },
    /* eslint-enable max-len */
    createTimeNoteAppointment: (startDate: moment.Moment, time: moment.Moment, clinicianId: string) =>
        /* eslint-disable max-len */
        dispatch(push({ pathname: `/calendar/${startDate.format(moment.HTML5_FMT.DATE)}/create-time-note`, query: { time: time.format(moment.HTML5_FMT.DATETIME_LOCAL), clinicianId } })),
    /* eslint-enable max-len */
    closeAppointment: (startDate) => dispatch(push(`/calendar/${startDate.format(moment.HTML5_FMT.DATE)}`)),
    selectClinic: (clinic, startDate) =>
        dispatch(push(`/calendar/${startDate.format(moment.HTML5_FMT.DATE)}/clinic/${clinic.id}`)),
    onSelectedUserIdsChanged: (userIds) => dispatch(updateCalendarSelectedUserIds(userIds)),
    onSelectedRoomIdsChanged: (userIds) => dispatch(updateCalendarSelectedRoomIds(userIds))
});

export const RoomAndAdminUserCalendar = connect(mapStateToProps, mapDispatchToProps)(RoomAndAdminUserCalendarUnconnected);
